import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { joinWaitlist } from '../lib/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ROLES = [
  { value: 'musician', label: 'Musician' },
  { value: 'engineer', label: 'Engineer' },
  { value: 'venue', label: 'Venue' },
  { value: 'other', label: 'Other' },
];

const fieldStyle = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: 17,
  color: '#F0EBE0',
  background: '#171411',
  borderRadius: 8,
  padding: '12px 14px',
  outline: 'none',
  width: '100%',
  marginTop: 12,
};

const consentLabelStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  marginTop: 16,
  fontFamily: "'Barlow', sans-serif",
  fontSize: 15,
  fontWeight: 400,
  color: '#a39889',
  lineHeight: 1.5,
  cursor: 'pointer',
};

const privacyLineStyle = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: 15,
  fontWeight: 400,
  color: '#a39889',
  marginTop: 12,
  lineHeight: 1.5,
};

export default function JoinPage() {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('musician');
  const [otherRole, setOtherRole] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [venueName, setVenueName] = useState('');
  const [notes, setNotes] = useState('');
  const [consentPilot, setConsentPilot] = useState(false);
  const [consentLaunchNotes, setConsentLaunchNotes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 'idle' | 'invalid' | 'success' | 'duplicate' | 'error'
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    const title = 'STAGE8 — Join the launch community';
    const prevTitle = document.title;
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc ? metaDesc.getAttribute('content') : null;
    if (metaDesc) metaDesc.setAttribute('content', title);
    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== null) metaDesc.setAttribute('content', prevDesc);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setStatus('invalid');
      return;
    }
    const honeypot = e.target.subject ? e.target.subject.value : '';
    setSubmitting(true);
    try {
      const { data } = await joinWaitlist({
        email: email.trim(),
        role,
        roleOther: role === 'other' ? (otherRole.trim() || null) : null,
        country: country.trim(),
        city: city.trim() || null,
        venueName: role === 'venue' ? (venueName.trim() || null) : null,
        notes: notes.trim() || null,
        consentPilot,
        consentLaunchNotes,
        company: honeypot,
      });
      setStatus(data && data.status === 'duplicate' ? 'duplicate' : 'success');
    } catch (err) {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (status === 'invalid') setStatus('idle');
  };

  const isConfirmed = status === 'success' || status === 'duplicate';

  return (
    <div
      className="join-page"
      data-testid="join-page"
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(1200px 600px at 15% 0%, rgba(200, 146, 62, 0.16), transparent 60%), radial-gradient(900px 500px at 85% 100%, rgba(200, 146, 62, 0.08), transparent 55%), #0E1110',
      }}
    >
      <style>{`
        .join-page ::placeholder { color: #6b6459; opacity: 1; }
        .join-page .join-email {
          border: 1px solid rgba(212, 169, 90, 0.20);
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .join-page .join-email:hover { border-color: rgba(212, 169, 90, 0.38); }
        .join-page .join-email:focus {
          border-color: #C8923E;
          box-shadow: 0 0 0 3px rgba(200, 146, 62, 0.28);
        }
        .join-page .join-submit {
          transition: filter 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;
        }
        .join-page .join-submit:hover { filter: brightness(1.08); }
        .join-page .join-submit:active { transform: translateY(1px); }
        .join-page .join-submit:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(200, 146, 62, 0.35), 0 0 0 5px rgba(240, 235, 224, 0.15);
        }
        .join-form-row { display: flex; flex-direction: column; gap: 12px; }
        @media (min-width: 640px) {
          .join-form-row { flex-direction: row; align-items: stretch; }
          .join-form-row .join-email { flex: 1 1 auto; }
          .join-form-row .join-submit { flex: 0 0 auto; }
        }
        .join-page .join-label {
          font-family: 'DM Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #a39889;
        }
        /* Role picker — one track with a gold thumb that slides to the selection.
           Critically damped curve (fast out, no overshoot); the label colour rides
           the same curve so the thumb lands before the text finishes darkening. */
        .join-page .join-roles {
          position: relative;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          margin-top: 8px;
          padding: 4px;
          border: 1px solid rgba(212, 169, 90, 0.20);
          border-radius: 10px;
          background: #171411;
        }
        .join-page .join-role-thumb {
          position: absolute;
          top: 4px;
          left: 4px;
          width: calc((100% - 8px) / 4);
          height: calc(100% - 8px);
          border-radius: 7px;
          background: #C8923E;
          transform: translateX(calc(var(--role-index, 0) * 100%));
          transition: transform 0.42s cubic-bezier(0.32, 0.72, 0, 1);
          will-change: transform;
          pointer-events: none;
        }
        .join-page .join-role {
          position: relative;
          z-index: 1;
          /* Leaving: lighten immediately, so the label never sits dark on the dark track. */
          transition: color 0.22s ease, transform 0.1s ease-out;
        }
        .join-page .join-role[aria-pressed='true'] {
          /* Arriving: hold the pale colour until the thumb is under the label. */
          transition: color 0.26s ease 0.1s, transform 0.1s ease-out;
        }
        .join-page .join-role:hover:not([aria-pressed='true']) { color: #F0EBE0; }
        .join-page .join-role:active { transform: scale(0.96); }
        @media (prefers-reduced-motion: reduce) {
          .join-page .join-role-thumb,
          .join-page .join-role,
          .join-page .join-role[aria-pressed='true'] { transition: none; }
        }
        .join-page .join-grid-2 { display: grid; grid-template-columns: 1fr; }
        @media (min-width: 480px) {
          .join-page .join-grid-2 { grid-template-columns: 1fr 1fr; gap: 12px; }
        }
        .join-page textarea.join-email { font-family: 'Barlow', sans-serif; }
        .join-page .join-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 40px;
          align-items: center;
          min-height: 88vh;
          padding: 56px 0;
        }
        @media (min-width: 960px) {
          .join-page .join-layout {
            grid-template-columns: 1.05fr 0.95fr;
            gap: 72px;
            padding: 0;
          }
        }
        .join-page .join-hero-title {
          font-family: var(--s8-font-display);
          font-weight: 600;
          font-synthesis: none;
          font-size: clamp(52px, 8.5vw, 104px);
          line-height: 1.15;
          letter-spacing: -0.01em;
          font-feature-settings: 'kern' 1;
          color: #F0EBE0;
          margin-top: 18px;
          text-wrap: balance;
        }
        @media (min-width: 960px) {
          /* Letters read too far apart as the display size grows. */
          .join-page .join-hero-title { letter-spacing: -0.005em; }
        }
        .join-page .join-card {
          position: relative;
          overflow: hidden;
          background: rgba(23, 20, 17, 0.72);
          border: none;
          border-radius: 16px;
          padding: 28px;
          backdrop-filter: blur(16px) saturate(140%);
          /* Shadow falls away from the light; a little of it spills back up
             onto the lit corner. */
          box-shadow: 14px 30px 70px -34px rgba(0, 0, 0, 0.9),
                      -5px -5px 28px -14px rgba(200, 146, 62, 0.28);
        }
        /* The stage light in the background catching the card's rim: a 1px
           gradient edge that follows the corner radius, brightest at the top
           left where the light lands and falling off toward bottom right. */
        .join-page .join-card::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          padding: 1px;
          background: linear-gradient(
            138deg,
            rgba(240, 205, 130, 0.92) 0%,
            rgba(200, 146, 62, 0.52) 14%,
            rgba(200, 146, 62, 0.18) 38%,
            rgba(212, 169, 90, 0.08) 70%,
            rgba(212, 169, 90, 0.06) 100%
          );
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          mask-composite: exclude;
          pointer-events: none;
        }
        .join-page .join-card-head { margin-bottom: 22px; }
        .join-page .join-card-title {
          font-family: var(--s8-font-display);
          font-weight: 600;
          font-synthesis: none;
          font-size: 26px;
          letter-spacing: -0.01em;
          color: #F0EBE0;
          line-height: 1.15;
        }
        .join-page .join-card-sub {
          font-family: 'Barlow', sans-serif;
          font-size: 16px;
          color: #a39889;
          margin-top: 6px;
        }
        .join-page .join-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 12px 6px 10px;
          border: 1px solid rgba(212, 169, 90, 0.30);
          border-radius: 999px;
          background: rgba(200, 146, 62, 0.06);
          font-family: 'DM Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #C8923E;
        }
        .join-page .join-live-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #C8923E;
          animation: joinPulse 2.4s ease-out infinite;
        }
        @keyframes joinPulse {
          0% { box-shadow: 0 0 0 0 rgba(200, 146, 62, 0.5); }
          70% { box-shadow: 0 0 0 8px rgba(200, 146, 62, 0); }
          100% { box-shadow: 0 0 0 0 rgba(200, 146, 62, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .join-page .join-live-dot { animation: none; }
        }
        .join-page .join-hero-title .accent { color: #C8923E; }

        /* ── Hero body copy ────────────────────────────────────────────────── */
        .join-page .join-hero { max-width: 36rem; }
        .join-page .join-lede {
          font-family: 'Barlow', sans-serif;
          font-size: 20px;
          font-weight: 400;
          line-height: 1.55;
          color: #F0EBE0;
          margin-top: 22px;
          text-wrap: balance;
        }
        .join-page .join-body {
          font-family: 'Barlow', sans-serif;
          font-size: 20px;
          font-weight: 400;
          line-height: 1.55;
          color: #cdbfa8;
          margin-top: 16px;
        }
        .join-page .join-close {
          font-family: 'Barlow', sans-serif;
          font-size: 20px;
          font-weight: 400;
          line-height: 1.55;
          color: var(--s8-amber);
          margin-top: 16px;
        }

        /* Entrance — hero only. The form is never delayed: it must be usable
           the instant the page paints. */
        .join-page .join-enter {
          opacity: 0;
          transform: translateY(10px);
          animation: joinRise 0.62s cubic-bezier(0.32, 0.72, 0, 1) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .join-page .join-enter { animation: none; opacity: 1; transform: none; }
        }
        @media (min-width: 640px) {
          .join-page .join-card { padding: 32px; }
        }
        /* Success "welcome to the community" moment */
        .join-page .join-in { text-align: center; padding: 8px 0 4px; }
        .join-page .join-seal {
          position: relative;
          width: 88px; height: 88px;
          margin: 6px auto 22px;
        }
        .join-page .join-seal::before {
          content: '';
          position: absolute; inset: -6px;
          border-radius: 50%;
          border: 2px solid rgba(200, 146, 62, 0.5);
          opacity: 0;
          animation: sealHalo 2.1s ease-out 0.8s infinite;
        }
        .join-page .join-seal-disc {
          position: absolute; inset: 0;
          border-radius: 50%;
          background: linear-gradient(145deg, #E7B65E, #C8923E 55%, #a9742c);
          box-shadow: 0 14px 34px -10px rgba(200, 146, 62, 0.6);
          opacity: 0;
          transform: scale(0.5);
          animation: sealPop 0.6s cubic-bezier(0.2, 0.9, 0.3, 1.35) forwards;
        }
        .join-page .join-seal-check {
          position: absolute; inset: 0;
          width: 88px; height: 88px;
          fill: none;
          stroke: #0E1110;
          stroke-width: 6;
          stroke-linecap: round;
          stroke-linejoin: round;
        }
        .join-page .join-seal-check path {
          stroke-dasharray: 56;
          stroke-dashoffset: 56;
          animation: sealCheck 0.45s ease-out 0.5s forwards;
        }
        .join-page .join-spark {
          position: absolute;
          width: 5px; height: 5px;
          border-radius: 50%;
          background: #E7B65E;
          opacity: 0;
          animation: sparkRise 1.5s ease-out forwards;
        }
        .join-page .join-spark.s1 { left: 4px;  bottom: 34px; animation-delay: 0.55s; }
        .join-page .join-spark.s2 { right: 6px; bottom: 30px; animation-delay: 0.72s; }
        .join-page .join-spark.s3 { left: 20px; bottom: 8px;  animation-delay: 0.86s; }
        .join-page .join-spark.s4 { right: 18px; bottom: 12px; animation-delay: 0.64s; }
        .join-page .join-spark.s5 { left: 44px; bottom: 2px;  animation-delay: 0.98s; }
        .join-page .join-badge {
          display: inline-flex;
          align-items: center;
          padding: 5px 13px;
          border: 1px solid rgba(212, 169, 90, 0.35);
          border-radius: 999px;
          background: rgba(200, 146, 62, 0.08);
          font-family: 'DM Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #C8923E;
          margin-bottom: 14px;
          opacity: 0;
          animation: joinRise 0.5s ease-out 0.9s forwards;
        }
        .join-page .join-in-title {
          font-family: var(--s8-font-display);
          font-weight: 600;
          font-synthesis: none;
          font-size: 50px;
          letter-spacing: -0.01em;
          color: #F0EBE0;
          line-height: 1.15;
          opacity: 0;
          transform: translateY(10px);
          animation: joinRise 0.6s ease-out 1.05s forwards;
        }
        .join-page .join-in-sub {
          font-family: 'Barlow', sans-serif;
          font-size: 15px;
          color: #d4c5a8;
          margin: 12px auto 0;
          max-width: 360px;
          line-height: 1.6;
          opacity: 0;
          transform: translateY(10px);
          animation: joinRise 0.6s ease-out 1.25s forwards;
        }
        @keyframes sealPop {
          0% { opacity: 0; transform: scale(0.5); }
          60% { opacity: 1; transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes sealHalo {
          0% { opacity: 0.55; transform: scale(0.92); }
          70% { opacity: 0; transform: scale(1.4); }
          100% { opacity: 0; transform: scale(1.4); }
        }
        @keyframes sealCheck { to { stroke-dashoffset: 0; } }
        @keyframes sparkRise {
          0% { opacity: 0; transform: translateY(0) scale(0.6); }
          25% { opacity: 1; }
          100% { opacity: 0; transform: translateY(-42px) scale(1); }
        }
        @keyframes joinRise { to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) {
          .join-page .join-seal-disc { opacity: 1; transform: none; animation: none; }
          .join-page .join-seal::before { animation: none; }
          .join-page .join-seal-check path { stroke-dashoffset: 0; animation: none; }
          .join-page .join-spark { opacity: 0; animation: none; }
          .join-page .join-badge,
          .join-page .join-in-title,
          .join-page .join-in-sub { opacity: 1; transform: none; animation: none; }
        }
      `}</style>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="join-layout">
          {/* Hero (main) */}
          <div className="join-hero">
            <div className="join-eyebrow join-enter" data-testid="join-eyebrow" style={{ animationDelay: '0.05s' }}>
              <span className="join-live-dot" aria-hidden="true" />
              EARLY ACCESS
            </div>

            <h1
              data-testid="join-headline"
              className="join-hero-title join-enter"
              style={{ animationDelay: '0.12s' }}
            >
              USE <span className="accent">STAGE8</span> FOR YOUR NEXT GIG
            </h1>

            <p className="join-lede join-enter" style={{ animationDelay: '0.24s' }}>
              Live music runs on assumptions and last-minute favours.
            </p>

            <p className="join-body join-enter" style={{ animationDelay: '0.32s' }}>
              STAGE8 puts the whole gig on one shareable page. Timeline, setlist,
              on-site contacts, who brings what, parking, dressing room.
            </p>

            <p className="join-close join-enter" style={{ animationDelay: '0.40s' }}>
              Confirmed by both sides, before showtime.
            </p>
          </div>

          {/* Details card (side) */}
          <div className="join-card" data-testid="join-card">
          {!isConfirmed && (
            <div className="join-card-head">
              <div className="join-card-title">Request early access</div>
              <div className="join-card-sub">We open access in small groups.</div>
            </div>
          )}
          {isConfirmed ? (
            <div data-testid="join-confirmation" style={{ marginTop: 0 }}>
              {status === 'success' ? (
                <div className="join-in">
                  <div className="join-seal" aria-hidden="true">
                    <span className="join-seal-disc" />
                    <svg className="join-seal-check" viewBox="0 0 88 88">
                      <path d="M26 45 l12 12 l24 -26" />
                    </svg>
                    <span className="join-spark s1" />
                    <span className="join-spark s2" />
                    <span className="join-spark s3" />
                    <span className="join-spark s4" />
                    <span className="join-spark s5" />
                  </div>
                  <div className="join-badge">Early access secured</div>
                  <div className="join-in-title">You&apos;re in the front row!</div>
                  <p className="join-in-sub">
                    Thanks for joining the launch community. We&apos;ll notify you the moment the doors open.
                  </p>
                </div>
              ) : (
                <p
                  style={{
                    fontFamily: "'Barlow', sans-serif",
                    fontSize: 15,
                    color: '#d4c5a8',
                    lineHeight: 1.55,
                  }}
                >
                  You&apos;re already on the list. Sit tight.
                </p>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="join-form" style={{ marginTop: 0 }}>
              {/* Role selector */}
              <div className="join-label">Your role</div>
              <div className="join-roles" role="group" aria-label="Your role">
                <span
                  className="join-role-thumb"
                  aria-hidden="true"
                  style={{ '--role-index': String(ROLES.findIndex((r) => r.value === role)) }}
                />
                {ROLES.map((r) => {
                  const active = role === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setRole(r.value)}
                      aria-pressed={active}
                      data-testid={`join-role-${r.value}`}
                      className="join-role"
                      style={{
                        fontFamily: "'Barlow Condensed', sans-serif",
                        fontSize: 15,
                        letterSpacing: '0.02em',
                        textTransform: 'uppercase',
                        padding: '10px 8px',
                        borderRadius: 7,
                        border: 'none',
                        background: 'none',
                        color: active ? '#0E1110' : '#d4c5a8',
                        cursor: 'pointer',
                        // Constant weight across states: a 500->700 swap would reflow the
                        // label mid-slide (and 700 has no real Barlow Condensed file).
                        fontWeight: 600,
                      }}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>

              {role === 'other' && (
                <input
                  className="join-email"
                  type="text"
                  name="role_other"
                  placeholder="Tell us your role (e.g. promoter, manager)"
                  required
                  value={otherRole}
                  onChange={(e) => setOtherRole(e.target.value)}
                  data-testid="join-role-other-input"
                  style={fieldStyle}
                />
              )}

              {/* Email */}
              <input
                className="join-email"
                type="text"
                name="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Email"
                value={email}
                onChange={handleEmailChange}
                data-testid="join-email-input"
                style={fieldStyle}
              />

              {/* City + Country */}
              <div className="join-grid-2">
                <input
                  className="join-email"
                  type="text"
                  name="city"
                  autoComplete="address-level2"
                  placeholder="City or region"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  data-testid="join-city-input"
                  style={fieldStyle}
                />
                <input
                  className="join-email"
                  type="text"
                  name="country"
                  autoComplete="country-name"
                  placeholder="Country"
                  required
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  data-testid="join-country-input"
                  style={fieldStyle}
                />
              </div>

              {/* Optional venue name — venues only */}
              {role === 'venue' && (
                <input
                  className="join-email"
                  type="text"
                  name="venue_name"
                  autoComplete="organization"
                  placeholder="Venue name (optional)"
                  value={venueName}
                  onChange={(e) => setVenueName(e.target.value)}
                  data-testid="join-venue-input"
                  style={fieldStyle}
                />
              )}

              {/* Optional notes */}
              <textarea
                className="join-email"
                name="notes"
                placeholder="What do you need STAGE8 for? (optional)"
                rows={3}
                maxLength={500}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="join-notes-input"
                style={{ ...fieldStyle, resize: 'vertical', minHeight: 84 }}
              />

              {/* Honeypot — visually hidden off-screen (not display:none).
                  Name must stay off browser/password-manager autofill schemas
                  ("company"/"organization" gets filled and silently drops the signup). */}
              <input
                type="text"
                name="subject"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: 1,
                  height: 1,
                  opacity: 0,
                  pointerEvents: 'none',
                }}
              />

              {/* Invalid email — inline, small, plain red under the input */}
              {status === 'invalid' && (
                <p
                  data-testid="join-email-error"
                  style={{
                    fontFamily: "'Barlow', sans-serif",
                    fontSize: 12,
                    color: '#C84A3C',
                    marginTop: 8,
                  }}
                >
                  Enter a valid email address.
                </p>
              )}

              {/* Network / server error */}
              {status === 'error' && (
                <p
                  data-testid="join-error"
                  style={{
                    fontFamily: "'Barlow', sans-serif",
                    fontSize: 12,
                    color: '#C84A3C',
                    marginTop: 8,
                  }}
                >
                  Something went wrong. Please try again.
                </p>
              )}

              {/* Consent A — pilot notice, required */}
              <label style={consentLabelStyle}>
                <input
                  type="checkbox"
                  required
                  checked={consentPilot}
                  onChange={(e) => setConsentPilot(e.target.checked)}
                  data-testid="join-consent-pilot"
                  style={{ accentColor: '#C8923E', marginTop: 2, flex: '0 0 auto' }}
                />
                <span>
                  Email me when my access is ready. (required)
                </span>
              </label>

              {/* Consent B — launch notes, optional */}
              <label style={{ ...consentLabelStyle, marginTop: 10 }}>
                <input
                  type="checkbox"
                  checked={consentLaunchNotes}
                  onChange={(e) => setConsentLaunchNotes(e.target.checked)}
                  data-testid="join-consent-launch"
                  style={{ accentColor: '#C8923E', marginTop: 2, flex: '0 0 auto' }}
                />
                <span>
                  Also send me occasional STAGE8 updates. (optional)
                </span>
              </label>

              <p style={privacyLineStyle}>
                See our{' '}
                <Link to="/privacy" style={{ color: 'var(--s8-amber)', textDecoration: 'underline' }}>
                  Privacy statement
                </Link>
                .
              </p>

              {/* Submit */}
              <button
                type="submit"
                className="join-submit"
                data-testid="join-submit-btn"
                disabled={submitting}
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  // Steps down on narrow phones so the label stays on one line.
                  fontSize: 'clamp(15px, 3.5vw, 17px)',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  padding: '14px 28px',
                  marginTop: 20,
                  width: '100%',
                  background: '#C8923E',
                  color: '#0E1110',
                  borderRadius: 6,
                  border: 'none',
                  cursor: submitting ? 'default' : 'pointer',
                  fontWeight: 600,
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? 'SAVING…' : 'REQUEST ACCESS →'}
              </button>
            </form>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
